/**
 * KPI grid — fixed accent layer + scrubbed scroll progress.
 *
 * @package HelloElementorChild
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  animateCounter,
  killCounterFrames,
  prefersReducedMotion,
  setCounterFinal,
} from './camst-counter.js';
import { initCamstReveal, teardownCamstReveal } from './camst-reveal.js';

gsap.registerPlugin(ScrollTrigger);

var kpiInstances = new WeakMap();
var accentTweens = new WeakMap();
var accentLayers = new WeakMap();
var ACCENT_DURATION = 1;
var ACCENT_LAYER_Z_INDEX = 1;
var ACCENT_COUNTER_THRESHOLD = 0.55;
var ACCENT_EASE = 'power2.inOut';

function mountAccentLayer(layer) {
  if (layer.parentElement !== document.body) {
    document.body.appendChild(layer);
  }

  layer.style.zIndex = String(ACCENT_LAYER_Z_INDEX);
}

function unmountAccentLayer(root, layer) {
  if (layer && layer.parentElement === document.body) {
    root.insertBefore(layer, root.firstChild);
  }
}

function setAccentProgress(root, layer, opacity) {
  var clamped = Math.max(0, Math.min(1, opacity));

  layer.style.opacity = String(clamped);
  root.style.setProperty('--camst-kpi-accent', String(clamped));
  root.classList.toggle('is-accent', clamped > 0.35);
  document.body.classList.toggle('camst-kpi-accent-active', clamped > 0.01);
}

function animateKpiCounters(root, reducedMotion) {
  root.querySelectorAll('[data-camst-counter]').forEach(function (counterEl) {
    if (counterEl.dataset.camstCounterDone === '1') {
      return;
    }

    counterEl.dataset.camstCounterDone = '1';
    animateCounter(counterEl, counterEl.getAttribute('data-value'), reducedMotion);
  });
}

function maybePlayCounters(root, opacity, reducedMotion) {
  if (opacity >= ACCENT_COUNTER_THRESHOLD && root.dataset.camstCountersPlayed !== '1') {
    root.dataset.camstCountersPlayed = '1';
    animateKpiCounters(root, reducedMotion);
  }

  if (opacity < 0.1) {
    delete root.dataset.camstCountersPlayed;
  }
}

function resetAccent(root, layer) {
  var tween = accentTweens.get(root);

  if (tween) {
    tween.kill();
    accentTweens.delete(root);
  }

  setAccentProgress(root, layer, 0);
  delete root.dataset.camstCountersPlayed;
}

function tweenAccentTo(root, layer, target, reducedMotion) {
  var tween = accentTweens.get(root);
  var state = {
    value: parseFloat(getComputedStyle(root).getPropertyValue('--camst-kpi-accent')) || 0,
  };

  if (tween) {
    tween.kill();
  }

  if (reducedMotion) {
    setAccentProgress(root, layer, target);

    if (target > 0) {
      maybePlayCounters(root, target, reducedMotion);
    }

    return;
  }

  tween = gsap.to(state, {
    value: target,
    duration: ACCENT_DURATION,
    ease: ACCENT_EASE,
    overwrite: true,
    onUpdate: function () {
      setAccentProgress(root, layer, state.value);
      maybePlayCounters(root, state.value, reducedMotion);
    },
  });

  accentTweens.set(root, tween);
}

export function initCamstKpiGrid(scope) {
  var roots = [];

  if (!scope) {
    roots = Array.prototype.slice.call(document.querySelectorAll('[data-camst-kpi-grid]'));
  } else if (scope.matches && scope.matches('[data-camst-kpi-grid]')) {
    roots = [scope];
  } else {
    roots = Array.prototype.slice.call(scope.querySelectorAll('[data-camst-kpi-grid]'));
  }

  roots.forEach(function (root) {
    if (root.dataset.camstKpiGridInit === '1') {
      return;
    }

    var layer = root.querySelector('[data-camst-kpi-accent-layer]');

    if (!layer) {
      return;
    }

    root.dataset.camstKpiGridInit = '1';
    mountAccentLayer(layer);
    accentLayers.set(root, layer);
    initCamstReveal(root);

    var reducedMotion = prefersReducedMotion();

    if (reducedMotion) {
      root.querySelectorAll('[data-camst-counter]').forEach(function (counterEl) {
        setCounterFinal(counterEl, counterEl.getAttribute('data-value'));
      });
    }

    var ctx = gsap.context(function () {
      ScrollTrigger.create({
        trigger: root,
        start: 'top center',
        end: 'bottom center',
        onEnter: function () {
          tweenAccentTo(root, layer, 1, reducedMotion);
        },
        onLeave: function () {
          tweenAccentTo(root, layer, 0, reducedMotion);
        },
        onEnterBack: function () {
          tweenAccentTo(root, layer, 1, reducedMotion);
        },
        onLeaveBack: function () {
          tweenAccentTo(root, layer, 0, reducedMotion);
        },
      });
    }, root);

    kpiInstances.set(root, ctx);
  });
}

export function teardownCamstKpiGrid(scope) {
  if (!scope) {
    return;
  }

  var roots = scope.matches && scope.matches('[data-camst-kpi-grid]')
    ? [scope]
    : Array.prototype.slice.call(scope.querySelectorAll('[data-camst-kpi-grid]'));

  roots.forEach(function (root) {
    var ctx = kpiInstances.get(root);
    var layer = accentLayers.get(root) || root.querySelector('[data-camst-kpi-accent-layer]');

    if (ctx) {
      ctx.revert();
      kpiInstances.delete(root);
    }

    killCounterFrames(root);
    teardownCamstReveal(root);

    if (layer) {
      resetAccent(root, layer);
      unmountAccentLayer(root, layer);
      accentLayers.delete(root);
    }

    root.querySelectorAll('[data-camst-counter]').forEach(function (counterEl) {
      delete counterEl.dataset.camstCounterDone;
    });

    delete root.dataset.camstKpiGridInit;
  });
}

export function refreshCamstKpiGrid() {
  ScrollTrigger.refresh();
}
